import { compareQuestIds } from '../domain/sort'
import type { CatalogIndex, RawCatalogs } from './types'

function byId<T extends { id: string }>(rows: T[]): Map<string, T> {
  return new Map(rows.map((row) => [row.id, row]))
}

export function indexCatalogs(raw: RawCatalogs): CatalogIndex {
  const craftables = byId(raw.items.craftables)
  const resources = byId(raw.items.resources)
  return {
    craftables,
    resources,
    items: new Map([...craftables, ...resources]),
    tools: byId(raw.tools),
    locations: byId(raw.locations),
    estates: byId(raw.estates),
    boosts: byId(raw.boosts),
    skins: byId(raw.skins),
    raw,
  }
}

/**
 * One row as a picker renders it. Every reference field in the editor is bound to
 * a list of these — the underlying id is never a free-text input.
 */
export interface PickerOption {
  id: string
  label: string
  /** Right-aligned metadata, e.g. "lvl 90 · 28g". */
  meta: string
  /** Extra searchable text beyond id and label. */
  keywords: string
  /** Renders a warning affordance — seasonal items can block a main-line quest. */
  warn?: string
}

export function itemOptions(catalog: CatalogIndex, kind: 'all' | 'craftable' = 'all'): PickerOption[] {
  const source = kind === 'craftable' ? catalog.craftables : catalog.items
  return [...source.values()]
    .map((item) => ({
      id: item.id,
      label: item.label,
      meta:
        item.unlockLevel === undefined
          ? `${item.saleBase}g`
          : `lvl ${item.unlockLevel} · ${item.saleBase}g`,
      keywords: `${item.type} ${item.craftableType ?? ''} ${item.tags.join(' ')}`,
      ...(item.seasonal ? { warn: 'seasonal' } : {}),
    }))
    .sort(sortByUnlockThenLabel(catalog))
}

/** Progression order is the useful order when judging an item for a quest. */
function sortByUnlockThenLabel(catalog: CatalogIndex) {
  return (a: PickerOption, b: PickerOption) => {
    const levelA = catalog.items.get(a.id)?.unlockLevel ?? -1
    const levelB = catalog.items.get(b.id)?.unlockLevel ?? -1
    if (levelA !== levelB) return levelA - levelB
    return a.label.localeCompare(b.label)
  }
}

export function toolOptions(catalog: CatalogIndex): PickerOption[] {
  return [...catalog.tools.values()].map((tool) => ({
    id: tool.id,
    label: tool.title,
    meta: `${tool.type} · ${tool.price}g`,
    keywords: tool.type,
  }))
}

export function locationOptions(catalog: CatalogIndex): PickerOption[] {
  return [...catalog.locations.values()].map((location) => ({
    id: location.id,
    label: location.title,
    meta: `${location.type} · diff ${location.difficulty}`,
    keywords: location.type,
  }))
}

export function estateOptions(catalog: CatalogIndex): PickerOption[] {
  return [...catalog.estates.values()].map((estate) => ({
    id: estate.id,
    label: estate.estateName,
    meta: `tier ${estate.tier} · ${estate.buildCost}g`,
    keywords: `tier${estate.tier}`,
  }))
}

export function boostOptions(catalog: CatalogIndex): PickerOption[] {
  return [...catalog.boosts.values()].map((boost) => ({
    id: boost.id,
    label: boost.label,
    meta: boost.type,
    keywords: `${boost.type} ${boost.boostId}`,
  }))
}

export function skinOptions(catalog: CatalogIndex): PickerOption[] {
  return [...catalog.skins.values()].map((skin) => ({
    id: skin.id,
    label: skin.label,
    meta: `${skin.skinType} · ${skin.rarity}`,
    keywords: `${skin.skinType} ${skin.rarity}`,
  }))
}

export function questOptions(quests: { id: string; label: string; gatingLevel: number }[]): PickerOption[] {
  return quests
    .map((quest) => ({
      id: quest.id,
      label: quest.label || quest.id,
      meta: `gate ${quest.gatingLevel}`,
      keywords: quest.id,
    }))
    .sort((a, b) => compareQuestIds(a.id, b.id))
}
